import * as React from "react";
import { Col, Row } from "react-bootstrap";
import classNames from "classnames";
import { useDroppable, useDragSelector } from "utils/dnd";
import { DropPosition } from "utils/dnd/types";
import { gettext } from "i18n";

interface WidgetDropZoneProps {
  rowIndex: number;
  widgetIndex: number;
  side: "left" | "right";
  isFirst: boolean;
  scope?: string;
  disabled?: boolean;
}

export const WidgetDropZone: React.FC<WidgetDropZoneProps> = ({
  rowIndex,
  widgetIndex,
  side,
  isFirst,
  scope,
  disabled = false,
}) => {
  const position: DropPosition = {
    type: "widget",
    rowIndex,
    widgetIndex,
    side,
    scope,
  };

  const { ref, isOver } = useDroppable({
    position,
    disabled,
  });

  if (disabled) {
    return null;
  }

  return (
    <div
      ref={ref as React.RefCallback<HTMLDivElement>}
      data-side={side}
      data-widget-index={widgetIndex}
      className={classNames("widget-drop-zone", {
        "drop-zone-active": isOver,
        "drop-zone-first": isFirst,
      })}
      style={{
        flex: "0 0 40px",
        maxWidth: "40px",
        minWidth: "40px",
        padding: "0 4px",
      }}
    >
      <div className="widget-placeholder">
        <span className="zone-label">
          {side === "left" ? "L" : "R"}
          {widgetIndex}
        </span>
      </div>
    </div>
  );
};

interface RowDropZoneProps {
  rowIndex: number;
  scope?: string;
  disabled?: boolean;
}

export const RowDropZone: React.FC<RowDropZoneProps> = ({
  rowIndex,
  scope,
  disabled = false,
}) => {
  const position: DropPosition = {
    type: "row",
    rowIndex,
    isNewRow: true,
    scope,
  };

  // Only offer new rows to widgets dragged from the same scope (station)
  const isDraggingInScope = useDragSelector(
    (state) =>
      state.isDragging &&
      (scope === undefined || state.dragPayload?.scope === scope),
  );
  const hidden = !isDraggingInScope || disabled;

  const { ref, isOver } = useDroppable({
    position,
    disabled: hidden,
  });

  if (hidden) {
    return null;
  }

  return (
    <Row
      ref={ref as React.RefCallback<HTMLDivElement>}
      className={classNames("row-drop-zone", {
        "drop-zone-active": isOver,
      })}
    >
      <div className="row-drop-indicator">
        <span>+ {gettext("New row")}</span>
      </div>
    </Row>
  );
};
